import React, { useEffect, useMemo } from "react";
import { Grid } from "@material-ui/core";
import { capitalize, isEmpty, isEqual, startCase, toLower } from "lodash";
import { tabNameToPath } from "../../../utils/strings.js";
import CustomCell from "../../generic/CustomCell.jsx";
import ItemInfo from "../../search/ItemInfo.jsx";
import { useDispatch } from "react-redux";
import { setUIData } from "../../../redux/slices/uiSlice.js";
import { usePrevious } from "../../../hooks/usePrevious.jsx";
import useResources from "../../../hooks/useResources.jsx";
import useResourceStatus from "../../../hooks/useResourceStatus.jsx";
import { getResourceAPI } from "../../../redux/slices/resourceSlice.js";


// Comment to set auto build
const TabPanel = ({
  cells,
  filteredTabData,
  tabName,
  selectedTradeCaseId,
  data,
  itemInfoFields,
}) => {
  const dispatch = useDispatch();

  const { remarks } = useResources();
  const { remarkStatus } = useResourceStatus();

  const { defaultRemark, defaultTerm } = useMemo(() => {
    const empty = { defaultRemark: "", defaultTerm: "" };

    if (!remarks) return empty;

    const foundValues = remarks.find((remark) => {
      const convertedState = remark.stage.toLowerCase();
      const convertedTabName = startCase(tabName)
        .toLowerCase()
        .replace(" ", "");

      return convertedState.includes(convertedTabName);
    });

    if (foundValues) {
      return {
        defaultRemark: foundValues.remarks,
        defaultTerm: foundValues.terms,
      };
    } else {
      return empty;
    }
  }, [remarks, tabName]);

  const prevTabData = usePrevious(filteredTabData);

  const reduxName = useMemo(
    () => `${tabNameToPath[tabName]}:${filteredTabData?.id}`,
    [filteredTabData?.id, tabName]
  );

  useEffect(() => {
    remarkStatus === "idle" && dispatch(getResourceAPI({ path: "remark" }));
  }, [dispatch, remarkStatus]);

  useEffect(() => {
    if (
      !reduxName ||
      isEmpty(filteredTabData) ||
      isEqual(prevTabData, filteredTabData)
    )
      return;

    const data = cells.reduce((acc, cell) => {
      const { dataField, defaultValue } = cell;

      let specialDefaultValue = "";
      if (dataField === "remarks") {
        specialDefaultValue = defaultRemark;
      } else if (dataField === "terms") {
        specialDefaultValue = defaultTerm;
      }
      const dbValue = filteredTabData[cell.dataField];
      return {
        ...acc,
        [dataField]: dbValue || defaultValue || specialDefaultValue,
      };
    }, {});

    dispatch(
      setUIData({
        name: reduxName,
        value: data,
      })
    );
  }, [
    cells,
    defaultRemark,
    defaultTerm,
    dispatch,
    filteredTabData,
    prevTabData,
    reduxName,
  ]);

  useEffect(() => {
    if (isEqual(prevTabData, filteredTabData)) return;

    dispatch(
      setUIData({
        name: "itemInfo",
        value: {
          vrt_transport_cost: filteredTabData.vrt_transport_cost,
          vrt_packing_cost: filteredTabData.vrt_packing_cost,
          discount: filteredTabData.discount,
          transport_cost: filteredTabData.transport_cost,
          packing_cost: filteredTabData.packing_cost,
          stage_id: filteredTabData.id,
          trade_case_id: selectedTradeCaseId,
          stage_type: `tp${(startCase(toLower(tabName))).replaceAll(" ", "")}`,
        },
      })
    );
  }, [dispatch, filteredTabData, prevTabData, selectedTradeCaseId, tabName]);

  return (
    <Grid
      container
      direction="row"
      alignItems="flex-start"
      justifyContent="flex-start"
      spacing={2}
    >
      {!isEmpty(filteredTabData) && selectedTradeCaseId && (
        <>
          <Grid
            item
            direction="column"
            xs={6}
            container
            alignItems="stretch"
            justifyContent="flex-start"
            spacing={1}
            style={{ height: "100%" }}
          >
            {cells
              .filter((cell) => cell.type !== "textarea")
              .map((cell, idx) => (
                <CustomCell
                  resourceName={cell.resourceName}
                  resourcePath={cell.resourcePath}
                  key={idx}
                  caption={cell.caption}
                  disabled={cell.disabled}
                  dataField={cell.dataField}
                  type={cell.type}
                  value={data[cell.dataField]}
                  reduxName={reduxName}
                />
              ))}
          </Grid>
          <Grid
            item
            direction="column"
            xs={6}
            container
            alignItems="stretch"
            justifyContent="flex-start"
            spacing={1}
            style={{ height: "100%" }}
          >
            {cells
              .filter((cell) => cell.type === "textarea")
              .map((cell, idx) => (
                <CustomCell
                  key={idx}
                  caption={cell.caption}
                  disabled={cell.disabled}
                  dataField={cell.dataField}
                  type={cell.type}
                  value={data[cell.dataField]}
                  reduxName={reduxName}
                />
              ))}
            {itemInfoFields && Object.keys(itemInfoFields).length > 1 && (
              <Grid item xs={12} style={{ marginTop: "auto" }}>
                Pricing:
                <ItemInfo itemInfoFields={itemInfoFields} />
              </Grid>
            )}
          </Grid>
        </>
      )}
    </Grid>
  );
};

export default TabPanel;
